<template>
  <div class="home-container">
    <common-header />

    <main class="home-main">
      <!-- Banner：180px，橙色渐变 + 标题居中；有轮播时在渐变叠层上展示 -->
      <div class="home-banner">
        <el-carousel
          v-if="carouselList.length > 0"
          arrow="always"
          :interval="4000"
          height="180px"
          class="home-banner__carousel"
        >
          <el-carousel-item v-for="(item, index) in carouselList" :key="index">
            <div class="home-banner__slide">
              <img
                :src="item.image"
                :alt="item.title || '轮播图'"
                class="home-banner__slide-img"
              />
              <div class="home-banner__overlay" />
              <h1 class="home-banner__title text-bold">作者投稿系统</h1>
            </div>
          </el-carousel-item>
        </el-carousel>
        <div v-else class="home-banner__fallback">
          <h1 class="home-banner__title text-bold">作者投稿系统</h1>
        </div>
      </div>

      <!-- 实时热榜：左公告 / 右多平台榜单 -->
      <section id="hot-rank" class="hot-board">
        <div class="flex align-center hot-title">
          <h2 class="hot-board__heading">实时热榜</h2>
          <div class="text-16 updated-at">更新时间：{{ updated_at }}</div>
        </div>
        <div class="hot-board__layout">
          <aside class="hot-board__ads" aria-label="公告与广告">
            <div class="hot-board__ads-title">公告</div>
            <ul v-if="announcementLines.length" class="hot-board__ads-list">
              <li
                v-for="(line, idx) in announcementLines"
                :key="idx"
                class="hot-board__ads-item"
              >
                {{ line }}
              </li>
            </ul>
            <el-empty
              v-else
              class="hot-board__ads-empty"
              description="暂无公告"
              :image-size="56"
            />
          </aside>
          <div class="hot-board__panel">
            <div class="hot-board__tabs" role="tablist">
              <button
                v-for="p in hotPlatforms"
                :key="p.key"
                type="button"
                role="tab"
                :aria-selected="activePlatform === p.key"
                :class="[
                  'hot-board__tab',
                  { 'is-active': activePlatform === p.key },
                ]"
                @click="onSelectHotPlatform(p.key)"
              >
                {{ p.label }}
              </button>
            </div>
            <div
              v-loading="leaderboardLoading"
              element-loading-text="加载中"
              element-loading-background="rgba(255, 255, 255, 0.65)"
              class="hot-board__list-body"
            >
              <el-empty
                v-if="!leaderboardLoading && !hotPlatforms.length"
                class="hot-board__empty"
                description="暂无热榜分类"
                :image-size="90"
              />
              <ol
                v-else-if="!leaderboardLoading && visibleRankRows.length"
                class="hot-board__rank-list"
              >
                <li
                  v-for="(row, rIdx) in visibleRankRows"
                  :key="`${activePlatform}-${rIdx}-${row.rank}`"
                  class="hot-board__rank-row"
                >
                  <span
                    class="hot-board__rank-num"
                    :class="{
                      'is-top3': row.rank <= 3,
                    }"
                    >{{ row.rank }}</span
                  >
                  <span class="hot-board__rank-title">{{ row.title }}</span>
                </li>
              </ol>
              <el-empty
                v-else-if="!leaderboardLoading"
                class="hot-board__empty"
                description="暂无榜单数据"
                :image-size="90"
              />
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- <section class="floor-bottom">
      <h3>投稿设计管理系统</h3>
      <p class="subtitle">让你的才华满城皆知</p>
      <p class="copyright">© 2025 投稿管理系统 · 保留所有权利</p>
    </section> -->
  </div>
</template>

<script>
import CommonHeader from "@/components/CommonHeader.vue";
import { getCarouselList } from "@/api/carousel";
import {
  getHomeNotice,
  getLeaderboardCategories,
  getLeaderboardItems,
} from "@/api/home";
import {
  normalizeNoticeLines,
  normalizeLeaderboardTabs,
  normalizeLeaderboardRows,
} from "@/utils/homeApiNormalize";

export default {
  name: "Home",
  components: {
    CommonHeader,
  },
  data() {
    return {
      carouselList: [],
      loading: false,
      /** 公告文案列表（来自 /home_notice/index） */
      announcementLines: [],
      /** 热榜 Tab（来自 /leaderboard/categories） */
      hotPlatforms: [],
      activePlatform: "",
      /** 当前分类完整榜单（来自 /leaderboard/items，前端展示前 30 条） */
      leaderboardRows: [],
      leaderboardLoading: false,
      updated_at: "",
    };
  },
  computed: {
    /** 当前 Tab：固定展示最多 30 条 */
    visibleRankRows() {
      return (this.leaderboardRows || []).slice(0, 30);
    },
  },
  watch: {
    $route(to) {
      if (to.path === "/hot") {
        this.scrollToHotBoard();
      }
    },
  },
  /** 公告接口在 created 即请求，避免仅依赖 mounted 时序或被合并代码时遗漏 */
  created() {
    this.fetchHomeNotice();
  },
  mounted() {
    this.fetchCarouselList();
    this.fetchLeaderboardBootstrap();
    this.$nextTick(() => {
      if (this.$route.path === "/hot") {
        this.scrollToHotBoard();
      }
    });
  },
  methods: {
    fetchCarouselList() {
      this.loading = true;
      getCarouselList()
        .then((response) => {
          this.loading = false;
          this.carouselList = response.data;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    /**
     * 公告 GET /home_notice/index
     * 兼容业务体在 res.data、res.list 或整段 res 上的多种返回格式。
     */
    fetchHomeNotice() {
      return getHomeNotice()
        .then((res) => {
          const payload = this.extractNoticePayload(res);
          const lines = normalizeNoticeLines(payload);
          this.announcementLines = lines.length ? lines : [];
        })
        .catch(() => {});
    },
    /** 从封装层返回的 res 中取出公告业务数据（与 axios 拦截器返回的 { code, msg, data } 对齐） */
    extractNoticePayload(res) {
      if (res == null || typeof res !== "object") {
        return null;
      }
      if (res.data != null) return res.data;
      if (res.list != null) return res.list;
      return res;
    },
    /** 拉取分类并加载首个分类下的榜单 */
    fetchLeaderboardBootstrap() {
      this.leaderboardLoading = true;
      getLeaderboardCategories()
        .then((res) => {
          const tabs = normalizeLeaderboardTabs(res.data);
          this.hotPlatforms = tabs;
          if (!tabs.length) {
            this.activePlatform = "";
            this.leaderboardRows = [];
            return;
          }
          this.activePlatform = tabs[0].key;
          return getLeaderboardItems({ category_id: tabs[0].key }).then((r) => {
            this.leaderboardRows = normalizeLeaderboardRows(r.data);
            this.updated_at = r.data.updated_at;
          });
        })
        .catch(() => {
          this.hotPlatforms = [];
          this.leaderboardRows = [];
          this.activePlatform = "";
        })
        .finally(() => {
          this.leaderboardLoading = false;
        });
    },
    /** 切换热榜 Tab */
    onSelectHotPlatform(key) {
      if (key === this.activePlatform) return;
      this.activePlatform = key;
      this.leaderboardLoading = true;
      getLeaderboardItems({ category_id: key })
        .then((res) => {
          this.leaderboardRows = normalizeLeaderboardRows(res.data);
        })
        .catch(() => {
          this.leaderboardRows = [];
        })
        .finally(() => {
          this.leaderboardLoading = false;
        });
    },
    scrollToHotBoard() {
      const el = document.getElementById("hot-rank");
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    },
  },
};
</script>

<style scoped>
.home-container {
  background: linear-gradient(180deg, #fff6f8 0%, #f3f7ff 100%);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.home-main {
  flex: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 0 32px;
  box-sizing: border-box;
}

/* —— Banner 180px —— */
.home-banner {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 8px 24px 0;
  box-sizing: border-box;
}

.home-banner__carousel {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 12px 32px rgba(255, 140, 80, 0.18);
}

.home-banner__slide {
  position: relative;
  width: 100%;
  height: 180px;
}

.home-banner__slide-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* 橙色渐变叠层，保留品牌暖色氛围 */
.home-banner__overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    125deg,
    rgba(255, 154, 86, 0.88) 0%,
    rgba(255, 120, 60, 0.82) 42%,
    rgba(255, 160, 95, 0.78) 100%
  );
  pointer-events: none;
}

.home-banner__title {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  margin: 0;
  z-index: 1;
  font-size: 32px;
  color: #fff;
  letter-spacing: 2px;
  text-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
  white-space: nowrap;
}

.home-banner__fallback {
  height: 90px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(125deg, #ffb347 0%, #ff7c43 38%, #ff9a56 100%);
  box-shadow: 0 12px 32px rgba(255, 140, 80, 0.22);
}

.home-banner__fallback .home-banner__title {
  position: static;
  transform: none;
}

/* —— 实时热榜 —— */
.hot-board {
  width: 100%;
  max-width: 1200px;
  margin: 24px auto 0;
  padding: 0 24px;
  box-sizing: border-box;
}

.hot-title {
  gap: 10px;
  align-items: center;
  margin: 0 0 16px;
}

.updated-at {
  margin-top: 4px;
  color: #999;
  font-size: 14px;
}

.hot-board__heading {
  font-size: 20px;
  font-weight: 700;
  color: #1f1f2f;
}

.hot-board__layout {
  display: flex;
  gap: 20px;
  align-items: stretch;
}

.hot-board__ads {
  flex: 0 0 220px;
  width: 220px;
  background: #fff;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 8px 24px rgba(31, 46, 134, 0.08);
  border: 1px solid rgba(232, 80, 120, 0.12);
}

.hot-board__ads-title {
  font-size: 15px;
  font-weight: 600;
  color: #e85078;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.hot-board__ads-list {
  margin: 0;
  padding-left: 18px;
  color: #555;
  font-size: 13px;
  line-height: 1.65;
}

.hot-board__ads-item {
  margin-bottom: 10px;
}

.hot-board__ads-item:last-child {
  margin-bottom: 0;
}

/* 左侧公告栏：空状态用 el-empty，略缩小图标以适配 220px 宽 */
.hot-board__ads-empty {
  padding: 4px 0 8px;
}

.hot-board__ads-empty ::v-deep .el-empty__description {
  margin-top: 8px;
}

/* 右侧榜单：空状态 */
.hot-board__empty {
  padding: 12px 0 24px;
}

.hot-board__empty ::v-deep .el-empty__description p {
  margin: 0;
  font-size: 14px;
}

.hot-board__panel {
  flex: 1;
  min-width: 0;
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px 20px;
  box-shadow: 0 8px 24px rgba(31, 46, 134, 0.08);
  border: 1px solid #eee;
}

.hot-board__tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.hot-board__tab {
  padding: 8px 18px;
  font-size: 14px;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  background: #fafafa;
  color: #444;
  cursor: pointer;
  transition: background 0.2s, border-color 0.2s, color 0.2s;
}

.hot-board__tab:hover {
  border-color: #e85078;
  color: #e85078;
}

.hot-board__tab.is-active {
  background: linear-gradient(135deg, #fff0f4 0%, #ffe8ef 100%);
  border-color: #e85078;
  color: #e85078;
  font-weight: 600;
}

/* Element UI v-loading 容器：切换平台 / 首屏拉取时保持高度，避免遮罩塌陷 */
.hot-board__list-body {
  position: relative;
  min-height: 160px;
}

.hot-board__rank-list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.hot-board__rank-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 8px;
  border-bottom: 1px solid #f5f5f5;
  font-size: 14px;
}

.hot-board__rank-row:last-child {
  border-bottom: none;
}

.hot-board__rank-num {
  flex: 0 0 28px;
  font-weight: 700;
  color: #999;
  text-align: center;
}

.hot-board__rank-num.is-top3 {
  color: #e85078;
}

.hot-board__rank-title {
  flex: 1;
  min-width: 0;
  color: #333;
  line-height: 1.5;
  word-break: break-word;
}

.hot-board__rank-heat {
  flex: 0 0 auto;
  font-size: 12px;
  color: #999;
}

@media (max-width: 900px) {
  .hot-board__layout {
    flex-direction: column;
  }

  .hot-board__ads {
    flex: none;
    width: 100%;
  }
}

.floor-bottom {
  background: #fff;
  text-align: center;
  padding: 24px 12px;
  box-shadow: 0 -12px 24px rgba(31, 46, 134, 0.08);
}

.floor-bottom h3 {
  font-size: 24px;
  margin: 0;
  color: #1f1f2f;
}

.floor-bottom .subtitle {
  margin-top: 8px;
  font-size: 16px;
  color: #666;
}

.floor-bottom .copyright {
  margin-top: 12px;
  font-size: 13px;
  color: #999;
}
</style>
